import { createAsyncThunk } from '@reduxjs/toolkit';
import { Vob, Id, VobClassificationsItem } from '@finpay-estimation/estimation-types';
import { admissionsAdvisorService } from '../../admissions-advisor/services/admissions-advisor-services';
import { patientHelper } from '../../patient/services/patient-service';
import { setSelectedVob, setSelectedVobLoading, setSelectedVobError, setPatientVobList, setPatientVobListLoading, setPatientVobListError, setVobClassifications, setVobClassificationsLoading, setVobClassificationsError } from './vob-slice';

// Load selected VOB
export const loadSelectedVob = createAsyncThunk(
  'vobContext/loadSelectedVob',
  async (config: { vobId: number | string }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setSelectedVobLoading(true));
      
      const response = await admissionsAdvisorService.getVobV3(Number(config.vobId));

      if (response.hasErrors) {
        const errorMessage = response.errorMessage || 'Failed to load VOB';
        dispatch(setSelectedVobError(errorMessage));
        patientHelper.robustErrorHandler(response, { rejectWithValue });
        return rejectWithValue(errorMessage);
      }

      dispatch(setSelectedVob(response.entity));
      dispatch(setSelectedVobLoading(false));

      return response.entity;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load VOB';
      dispatch(setSelectedVobError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

// Save selected VOB (create or update)
export const saveSelectedVob = createAsyncThunk(
  'vobContext/saveSelectedVob',
  async (config: { vob: Vob; vobId?: Id }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setSelectedVobLoading(true));
      
      let response;
      if (config.vobId) {
        // Update existing VOB
        response = await admissionsAdvisorService.updateVobV3(Number(config.vobId), config.vob);
      } else {
        // Create new VOB
        response = await admissionsAdvisorService.newVobV3(config.vob);
      }

      if (response.hasErrors) {
        const errorMessage = response.errorMessage || 'Failed to save VOB';
        dispatch(setSelectedVobError(errorMessage));
        patientHelper.robustErrorHandler(response, { rejectWithValue });
        return rejectWithValue(errorMessage);
      }

      dispatch(setSelectedVob(response.entity));
      dispatch(setSelectedVobLoading(false));

      return response.entity;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to save VOB';
      dispatch(setSelectedVobError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

// Create new VOB
export const createSelectedVob = createAsyncThunk(
  'vobContext/createSelectedVob',
  async (vob: Vob, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setSelectedVobLoading(true));
      
      const response = await admissionsAdvisorService.newVobV3(vob);

      if (response.hasErrors) {
        const errorMessage = response.errorMessage || 'Failed to create VOB';
        dispatch(setSelectedVobError(errorMessage));
        patientHelper.robustErrorHandler(response, { rejectWithValue });
        return rejectWithValue(errorMessage);
      }

      dispatch(setSelectedVob(response.entity));
      dispatch(setSelectedVobLoading(false));

      return response.entity;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to create VOB';
      dispatch(setSelectedVobError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

// Load patient VOB list
export const loadPatientVobList = createAsyncThunk(
  'vobContext/loadPatientVobList',
  async (patientId: number, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setPatientVobListLoading(true));
      
      const response = await admissionsAdvisorService.getVobsByPatientId(patientId);

      if (response.hasErrors) {
        const errorMessage = response.errorMessage || 'Failed to load patient VOBs';
        dispatch(setPatientVobListError(errorMessage));
        patientHelper.robustErrorHandler(response, { rejectWithValue });
        return rejectWithValue(errorMessage);
      }

      dispatch(setPatientVobList(response.entity));
      dispatch(setPatientVobListLoading(false));

      return response.entity;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load patient VOBs';
      dispatch(setPatientVobListError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
);

export const loadVobClassifications = createAsyncThunk<
  VobClassificationsItem[],
  number,
  { rejectValue: string }
>(
  'vobContext/loadVobClassifications',
  async (paramId: number, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setVobClassificationsLoading(true));
      
      const response = await admissionsAdvisorService.getVobClassifications(paramId);

      if (response.hasErrors) {
        const errorMessage = response.errorMessage || 'Failed to load VOB classifications';
        dispatch(setVobClassificationsError(errorMessage));
        patientHelper.robustErrorHandler(response, { rejectWithValue });
        return rejectWithValue(errorMessage);
      }

      dispatch(setVobClassifications(response.entity));
      dispatch(setVobClassificationsLoading(false));

      return response.entity;
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load VOB classifications';
      dispatch(setVobClassificationsError(errorMessage));
      return rejectWithValue(errorMessage);
    }
  }
); 